import { useEffect, useRef, useState } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { Link } from 'react-router-dom';
import { permissionState } from '../lib/notifications';
// ⚠️ SHARED WITH THE FULL PAGE, not redeclared here — see lib/notificationDisplay.
import { sectionLabel, ago } from '../lib/notificationDisplay';
import './NotificationPanel.css';

// The dropdown behind the top-bar bell. Rendered only while open (AppShell
// takes a render prop for exactly that reason), so its list costs nothing on
// every page that merely has a bell.

function NotificationPanel({ close }) {
  const {
    items, counts, loading, openNotification, markAllRead, enableDesktop, testDesktop,
    hasMore, loadingMore, loadMore,
  } = useNotifications();
  const perm = permissionState();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState('');

  /**
   * Report what actually happened, naming the failure rather than saying "sent".
   *
   * ⚠️ THE COMMONEST OUTCOME IS A SILENT SUCCESS. The service worker suppresses
   * a push while a window of this origin is visible and focused — deliberately,
   * so nobody gets an OS toast on top of the in-app one — which means testing
   * with the app in front of you shows nothing at all. Saying so here is the
   * difference between "it works, minimise the window" and a bug report.
   */
  const runTest = async () => {
    setTesting(true);
    setTestResult('');
    try {
      const r = await testDesktop();
      if (!r.configured) {
        setTestResult('The server has no push keys configured, so nothing can be sent.');
      } else if (!r.subscriptions) {
        setTestResult('This browser is not registered for push yet. Reload the page and try again.');
      } else if (!r.sent) {
        setTestResult('The push service rejected it. The registration may have expired — reload and try again.');
      } else {
        setTestResult(`Sent to ${r.sent} browser${r.sent === 1 ? '' : 's'}. Switch away from this window — it stays silent while the app is focused.`);
      }
    } catch {
      setTestResult('Could not reach the server.');
    } finally {
      setTesting(false);
    }
  };

  // Which rows were unread at the instant the panel opened.
  //
  // Opening the panel clears the badge (see below), which would otherwise wipe
  // every visual trace of what was new in the same frame the user started
  // reading it. Snapshotting first keeps the bold rows and their dots for as
  // long as the panel stays open, so the badge can be honest — you HAVE now
  // been shown these — without the list forgetting which ones you were being
  // shown. Closing and reopening deliberately gives a clean list.
  const [wasUnread] = useState(
    () => new Set(items.filter((n) => !n.readAt).map((n) => n.id))
  );

  // Guarded by a ref rather than an empty dep array alone: React 18's StrictMode
  // runs effects twice in development, and this one performs a write.
  const cleared = useRef(false);
  useEffect(() => {
    if (cleared.current || !counts.total) return;
    cleared.current = true;
    markAllRead();
  }, [counts.total, markAllRead]);

  // ── Reading an announcement without leaving the bell ──────────────────────
  //
  // ⚠️ A ROW WITH NO `path` HAS NOWHERE TO SEND ANYBODY, and until now clicking
  // one closed the panel and did nothing else — the notification simply
  // vanished. Announcements are the main case (a broadcast belongs to no
  // section of the app, so there is deliberately nothing to go and look at),
  // and they are precisely the rows whose whole content IS the message. Worse,
  // the body is clamped to one line here, so the thing the firm was told was
  // readable nowhere except the full-page feed.
  //
  // So those rows expand in place instead. The ones that DO link still
  // navigate, because the page they open shows the record this is about and
  // expanding a summary of it would be the lesser half.
  const [expandedId, setExpandedId] = useState(null);

  const handleOpen = (n) => {
    if (!n.path) {
      // Still marked read — `openNotification` does that and then declines to
      // navigate for want of a path, which is exactly what is wanted here.
      openNotification(n);
      setExpandedId((cur) => (cur === n.id ? null : n.id));
      return;
    }
    openNotification(n);
    close?.();
  };

  return (
    <div className="notif-panel" role="dialog" aria-label="Notifications">
      {/* No "Mark all read" control: opening this panel already does it. A
          button that is permanently disabled the instant its own container
          renders is worse than no button. */}
      <div className="notif-panel-head">
        <span className="notif-panel-title">Notifications</span>
        {/* ⚠️ THE ONLY WAY TO THE FULL PAGE, DELIBERATELY. It carries no nav
            entry: a person's own feed is not a section of the firm's cockpit,
            and a sidebar row for it would sit among nine things that ARE while
            being the one nobody navigates to on purpose. You get here from the
            bell you were already looking at. */}
        <Link className="notif-panel-all" to="/notifications" onClick={close}>
          View all
        </Link>
      </div>

      {/* The permission ask lives HERE rather than firing on login, because the
          browser grants exactly one prompt per origin and a blocked one is
          recoverable only through the address-bar padlock. Somebody who has
          just opened this panel is asking the question themselves. */}
      {perm === 'default' && (
        <button type="button" className="notif-panel-enable" onClick={enableDesktop}>
          <span aria-hidden="true">🔔</span>
          Enable desktop notifications
        </button>
      )}
      {perm === 'denied' && (
        <p className="notif-panel-blocked">
          Desktop notifications are blocked for this site. Turn them back on from the
          padlock icon in the address bar.
        </p>
      )}
      {/* Stated explicitly rather than left to silence. Without it there is no
          way to tell "switched on and nothing has happened yet" apart from
          "never switched on" — and the toast only fires while this tab is in the
          background, so the quiet case is the normal one. */}
      {/* ⚠️ "Granted" is the BROWSER's answer, and the browser answers it
          locally without ever contacting us — so this line was reassuring
          whether or not the server had a subscription to push to. It said
          notifications were on while a broken subscribe call meant nobody was
          registered, which is why the test below exists: it asks the SERVER. */}
      {perm === 'granted' && (
        <div className="notif-panel-status">
          <p>
            Desktop notifications are on — including when this tab is closed, as long as
            the browser is still running.
          </p>
          <button type="button" className="notif-panel-test" onClick={runTest} disabled={testing}>
            {testing ? 'Sending…' : 'Send a test notification'}
          </button>
          {testResult && <p className="notif-panel-testresult">{testResult}</p>}
        </div>
      )}

      <div className="notif-panel-list">
        {loading && !items.length && <p className="notif-panel-empty">Loading…</p>}

        {!loading && !items.length && (
          <p className="notif-panel-empty">
            Nothing yet. Assignments, client requests and messages show up here.
          </p>
        )}

        {items.map((n) => {
          const readable = !n.path;
          const open = readable && expandedId === n.id;
          return (
            <button
              type="button"
              key={n.id}
              className={`notif-row${wasUnread.has(n.id) || !n.readAt ? ' notif-row--unread' : ''}`
                + `${readable ? ' notif-row--readable' : ''}${open ? ' notif-row--open' : ''}`}
              onClick={() => handleOpen(n)}
              // Announced as a disclosure only when it is one; on a row that
              // navigates, `aria-expanded` would promise an expansion that
              // never comes.
              aria-expanded={readable ? open : undefined}
            >
              <span className="notif-row-dot" aria-hidden="true" />
              <span className="notif-row-main">
                <span className="notif-row-title">{n.title}</span>
                {n.body && (
                  <span className={`notif-row-body${open ? ' notif-row-body--full' : ''}`}>
                    {n.body}
                  </span>
                )}
                <span className="notif-row-meta">
                  {sectionLabel(n.section)} · {ago(n.createdAt)}
                  {/* Says which rows open, and which are already all there. A
                      caret alone would be a control with no stated effect. */}
                  {readable && n.body && (
                    <span className="notif-row-hint">{open ? ' · tap to close' : ' · tap to read'}</span>
                  )}
                </span>
              </span>
              {readable && n.body && (
                <span className="notif-row-caret" aria-hidden="true">›</span>
              )}
            </button>
          );
        })}

        {hasMore && (
          <button
            type="button"
            className="notif-panel-more"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load older'}
          </button>
        )}

        {/* Offered once there IS a history to search — the dropdown can only
            page backwards, and past a screenful "find the one about Mehta" is a
            search, not a scroll. */}
        {hasMore && (
          <Link className="notif-panel-searchlink" to="/notifications" onClick={close}>
            Search all notifications
          </Link>
        )}
      </div>
    </div>
  );
}

export default NotificationPanel;
